<template>
  <div class="pdfLayout">
    <div class="layoutContainer">
      <div class="layoutSetting">
        <div class="btnExport">
          <button type="button" class="exportJson" @click="downloadJson"></button>
          <button type="button" class="exportPdf" @click="printPdf"></button>
        </div>
        <div class="formGroup mb30">
          <label class="label groupLabel mb5">출력 언어</label>
          <div>
            <select class="input select inlineInput" v-model="pdfLayout.itemNameLocale">
              <option v-for="lang in itemNameLocaleOptions"
                :key="lang.c"
                :value="lang.c">
                {{ lang.n }}
              </option>
            </select>
          </div>
        </div>
        <div class="formGroup mb30">
          <label class="label groupLabel mb5">출력 카테고리</label>
          <div class="pdfCategoryList">
            <span v-for="category in pdfCategoryGroups"
              :key="category.categoryId"
              class="checkbox">
              <input type="checkbox" :id="`check_category_${category.categoryId}`" :value="category.categoryId" v-model="selectedCategoryIds" />
              <label :for="`check_category_${category.categoryId}`">{{ category.categoryName[pdfLayout.itemNameLocale] }}</label>
            </span>
          </div>
        </div>
        <div class="formGroup mb30">
          <label class="label groupLabel mb5">출력 템플릿</label>
          <div class="setTemplate">
            <div class="templateItem">
              <div>컬럼 수</div>
              <div>
                <input type="text" class="input" v-model="pdfLayout.columnCount">
              </div>
            </div>
            <div class="templateItem">
              <div>컬럼 간격</div>
              <div>
                <input type="text" class="input" v-model="pdfLayout.columnGap">
              </div>
            </div>
            <div class="templateItem">
              <div>글자 크기</div>
              <div>
                <input type="text" class="input" v-model="pdfLayout.fontSize">
              </div>
            </div>
            <div class="templateItem">
              <div>이름 정렬</div>
              <div class="btns">
                <button type="button" class="btnAlign left" :class="{active: pdfLayout.nameAlign === 'left'}" @click="setNameAlign('left')"></button>
                <button type="button" class="btnAlign center" :class="{active: pdfLayout.nameAlign === 'center'}" @click="setNameAlign('center')"></button>
                <button type="button" class="btnAlign right" :class="{active: pdfLayout.nameAlign === 'right'}" @click="setNameAlign('right')"></button>
              </div>
            </div>
            <div class="templateItem">
              <div>금액 정렬</div>
              <div class="btns">
                <button type="button" class="btnAlign left" :class="{active: pdfLayout.priceAlign === 'left'}" @click="setPriceAlign('left')"></button>
                <button type="button" class="btnAlign center" :class="{active: pdfLayout.priceAlign === 'center'}" @click="setPriceAlign('center')"></button>
                <button type="button" class="btnAlign right" :class="{active: pdfLayout.priceAlign === 'right'}" @click="setPriceAlign('right')"></button>
              </div>
            </div>
          </div>
        </div>
        <div class="formGroup mb30">
          <label class="label groupLabel mb5">커버 페이지</label>
          <div>
            <div class="mb5">
              <span class="checkbox">
                <input type="checkbox" id="use_cover" v-model="pdfLayout.useCover" />
                <label for="use_cover">사용</label>
              </span>
            </div>
            <div v-if="pdfLayout.useCover" class="coverPage">
              <SetLink
                :locale="locale"
                :link="pdfLayout.coverPage"
                :fix="['page']" />
            </div>
          </div>
        </div>
        <div class="formGroup mb30">
          <label class="label groupLabel mb5">워터마크</label>
          <div>
            <div class="mb5">
              <span class="checkbox">
                <input type="checkbox" id="use_watermark" v-model="pdfLayout.useWatermark" />
                <label for="use_watermark">사용</label>
              </span>
            </div>
            <div v-if="pdfLayout.useWatermark">
              <div v-if="pdfLayout.watermarkImage" class="setWatermark">
                <div>
                  <div>워터마크 이미지</div>
                  <div class="watermarkImage">
                    <img :src="pdfLayout.watermarkImage" alt="" />
                    <button type="button" class="btnDel" @click="deleteWatermark"><img src="~@/assets/img/ico_close.svg" alt=""></button>
                  </div>
                </div>
                <div class="watermarkPosition">
                  <div>워터마크 위치</div>
                  <div class="watermarkPositionPicker">
                    <button
                      v-for="position in watermarkPositions"
                      :key="position"
                      type="button"
                      :class="[position, {active: pdfLayout.watermarkPosition === position} ]"
                      @click="pdfLayout.watermarkPosition = position"
                    >
                    </button>
                  </div>
                </div>
                <div class="watermarkSize">
                  <div>워터마크 사이즈</div>
                  <div class="unitInput size80">
                    <input type="text" class="input" v-model="pdfLayout.watermarkSize">
                    <div class="unit">px</div>
                  </div>
                </div>
              </div>
              <div v-else>
                <div class="mb5">워터마크 이미지</div>
                <label class="btn btnWhite toUppercase ">{{locale.upload}}<input type="file" @change="uploadImage($event)"></label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="layoutPreview" ref="printTarget" :class="{ isPaginating }" :style="{ fontSize: `${pdfLayout.fontSize}px` }">
        <section
          v-for="(page, pageIndex) in printPages"
          :key="pageIndex"
          class="pdfPage">
          <span v-if="pdfLayout.useWatermark && pdfLayout.watermarkImage" class="watermark" :style="getWatermarkStyle('px')">
            <img :src="pdfLayout.watermarkImage" alt="" :style="{ maxWidth: `${pdfLayout.watermarkSize}px` }" />
          </span>

          <div
            v-for="group in page.groups"
            :key="group.id"
            class="pdfItemRowBlock">
            <div v-if="group.showCategoryLabel" class="pdfCategoryLabel">
              {{ group.categoryName[pdfLayout.itemNameLocale] }}
            </div>

            <div
              class="pdfItemRow"
              :style="{
                gridTemplateColumns: `repeat(${pdfLayout.columnCount}, 1fr)`,
                gap: `${pdfLayout.columnGap}px`
              }">
              <div v-for="item in group.items"
                :key="item.shopItemId"
                class="pdfItem">
                <div class="thumb" :style="{ backgroundImage: `url(${item.thumb})` }"></div>

                <div class="name" :class="`align_${pdfLayout.nameAlign}`">
                  {{ item.shopItemName && item.shopItemName[pdfLayout.itemNameLocale] }}
                </div>

                <div class="price" :class="`align_${pdfLayout.priceAlign}`">
                  {{ priceFormat(displayPrice(item.priceInfo)) }}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import UNIQUE from '@/common/unique'
import regExp from '@/common/regExp'
import SetLink from '@/components/Util/SetLink'

export default {
  name: 'ShopPdfLayout',
  props: [
    'id',
    'locale'
  ],
  components: {
    SetLink
  },
  computed: {
    shopPageId () {
      return this.id
    },
    defaultLang () {
      const langInfo = this.$store.state.setting.lang
      return langInfo.default
    },
    itemNameLocaleOptions () {
      const { default: defaultLang, manual, info } = this.$store.state.setting.lang
      const langCodes = Array.from(new Set([
        defaultLang,
        ...(manual || [])
      ]))
      return langCodes
        .map(code => info.find(lang => lang.c === code))
        .filter(Boolean)
    },
    rowStyle() {
      return {
        gridTemplateColumns: `repeat(${this.pdfLayout.columnCount}, 1fr)`,
        gap: `${this.pdfLayout.columnGap}px`
      }
    },
    previewStyle() {
      return {
        fontSize: `${this.pdfLayout.fontSize}px`
      }
    },
    coverUrl () {
      const siteid = this.$store.getters['authentication/getUserInfo']?.siteid
      const useCover = this.pdfLayout.useCover
      const pageId = this.pdfLayout.coverPage.pageId
      const url = `https://edit-test.istay.io/viewer/index.html?site=${siteid}&frame=false#ko/page/${pageId}`
      return useCover ? url : null
    }
  },
  watch: {
    selectedCategoryIds () {
      if (!this.pdfCategoryGroups.length) return
      this.rebuildPrintPages()
    },

    'pdfLayout.columnCount'() {
      this.rebuildItemRows()
      this.rebuildPrintPages()
    },
    'pdfLayout.columnGap': 'rebuildPrintPages',
    'pdfLayout.fontSize': 'rebuildPrintPages',
    'pdfLayout.itemNameLocale': 'rebuildPrintPages',
  },
  async created () {
    this.pdfLayout.itemNameLocale = this.defaultLang
    this.pdfLayout.watermarkImage = this.$store.state.setting.site.logo
    this.$store.commit('onLoading', true)
    await this.getPdfLayoutData()
    this.getShopPage()
    this.$store.commit('onLoading', false)
  },
  data () {
    return {
      shopPageInfo: null,
      isPaginating: false,
      pdfLayout: {
        // coverUrl: 'https://edit-test.istay.io/viewer/index.html?site=sqa&frame=false#ko/page/sqa.4ee3e562-ae0a-8291-20b3-b93a5886fadb',
        columnCount: 3,
        columnGap: 16,
        fontSize: 16,
        nameAlign: 'center',
        priceAlign: 'center',
        itemNameLocale: '',
        useWatermark: false,
        watermarkImage: '',
        watermarkPosition: 'tr',
        watermarkSize: '120',
        useCover: false,
        coverPage: {
          type: 'page',
          pageId: ''
        }
      },
      leafCategories: [],
      selectedCategoryIds: [],
      pdfCategoryGroups: [],
      printPages: [],
      watermarkPositions: [
        'tl', 'tc', 'tr', 'bl', 'bc', 'br'
      ]
    }
  },
  methods: {
    displayPrice (priceInfo) {
      const excludeTax = this.shopPageInfi?.excludeTax
      return excludeTax ? priceInfo?.net : priceInfo?.gross
    },
    priceFormat (price) {
      const currency = this.shopCurrency || this.$store.getters['setting/getCurrencyCode']
      const disabledSymbol = this.$store.getters['setting/getData']?.currency?.disabledCurrencySymbol ?? false
      return regExp.priceFormat(price, currency.toUpperCase(), disabledSymbol)
    },
    uploadImage (e) {
      const file = e.target.files[0]

      if (!file) {
        return
      }

      const data = {
        id: UNIQUE.mid(),
        file
      }

      const ext = file.name
        .slice(file.name.lastIndexOf('.') + 1)
        .toLowerCase()

      const isImage = regExp.image.test(ext)

      if (!isImage) {
        alert(this.locale.validate_upload_image)
        e.target.value = ''
        return
      }

      this.$store.dispatch('util/uploadFile', data).then((res) => {
        this.pdfLayout.watermarkImage = res.source
        e.target.value = ''
      })
    },
    setNameAlign (align) {
      this.pdfLayout.nameAlign = align
    },
    setPriceAlign (align) {
      this.pdfLayout.priceAlign = align
    },
    deleteWatermark () {
      this.pdfLayout.watermarkImage = ''
    },
    getWatermarkStyle (unit = 'px') {
      const position = this.pdfLayout.watermarkPosition
      const offset = unit === 'mm' ? '6mm' : '20px'

      return {
        tl: {
          top: offset,
          left: offset
        },
        tc: {
          top: offset,
          left: '50%',
          transform: 'translateX(-50%)'
        },
        tr: {
          top: offset,
          right: offset
        },
        bl: {
          bottom: offset,
          left: offset
        },
        bc: {
          bottom: offset,
          left: '50%',
          transform: 'translateX(-50%)'
        },
        br: {
          bottom: offset,
          right: offset
        }
      }[position]
    },
    getExportFileName () {
      const now = new Date()
      const yyyy = now.getFullYear()
      const mm = String(now.getMonth() + 1).padStart(2, '0')
      const dd = String(now.getDate()).padStart(2, '0')
      const hh = String(now.getHours()).padStart(2, '0')
      const min = String(now.getMinutes()).padStart(2, '0')
      const datetime = `${yyyy}${mm}${dd}_${hh}${min}`
      return `${this.shopPageInfo?.pageName}_${this.pdfLayout.itemNameLocale}_${datetime}` || this.shopPageId
    },
    styleObjectToCss (style) {
      return Object.entries(style || {})
        .map(([key, value]) => {
          const cssKey = key.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`)
          return `${cssKey}: ${value};`
        })
        .join('\n')
    },
    flattenLeafCategories (categories) {
      const result = []

      const traverse = (category, breadcrumbs = []) => {
        const currentBreadcrumbs = [
          ...breadcrumbs,
          {
            categoryId: category.categoryId,
            categoryName: category.categoryLabel
          }
        ]

        if (!category.subCategory?.length) {
          result.push({
            categoryId: category.categoryId,
            categoryName: category.categoryLabel,
            breadcrumbs: currentBreadcrumbs
          })
          return
        }

        category.subCategory.forEach(subCategory => {
          traverse(subCategory, currentBreadcrumbs)
        })
      }

      categories.forEach(category => {
        traverse(category)
      })

      return result
    },
    chunkItems (items, columnCount) {
      const size = Number(columnCount) || 1
      const rows = []

      for (let i = 0; i < items.length; i += size) {
        rows.push(items.slice(i, i + size))
      }

      return rows
    },
    buildRowGroups (categories) {
      const groups = []

      categories.forEach(category => {
        category.itemRows.forEach((row, rowIndex) => {
          groups.push({
            id: `${category.categoryId}-${rowIndex}`,
            categoryId: category.categoryId,
            categoryName: category.categoryName,
            showCategoryLabel: row.showCategoryLabel,
            items: row.items
          })
        })
      })

      return groups
    },
    async getPdfLayoutData () {
      this.$store.commit('onLoading', true)

      try {
        const shopCategory = await this.$store.dispatch('shop/getShopCategory', this.shopPageId)
        const leafCategories = this.flattenLeafCategories(shopCategory?.message?.categories || [])

        this.leafCategories = leafCategories
        this.selectedCategoryIds = leafCategories.map(category => category.categoryId)

        const pdfCategoryGroups = []

        for (const category of leafCategories) {
          const response = await this.$store.dispatch('shop/getShopItems', {
            pageId: this.shopPageId,
            categoryId: category.categoryId
          })

          const shopItems = response?.message?.shopItems?.filter(item => item.isActive) || []

          const items = shopItems.map(item => ({
            shopItemId: item.shopItemId,
            shopItemName: item.shopItemName,
            thumb: item.thumb || '',
            priceInfo: item.priceInfo,
            ...item.pId && {
              pId: item.pId
            }
          }))

          if (!items.length) {
            continue
          }

          const itemRows = this.chunkItems(items, this.pdfLayout.columnCount)
            .map((row, index) => ({
              showCategoryLabel: index === 0,
              items: row
            }))

          pdfCategoryGroups.push({
            ...category,
            items,
            itemRows
          })
        }

        this.pdfCategoryGroups = pdfCategoryGroups
        this.selectedCategoryIds = pdfCategoryGroups.map(category => {
          return category.categoryId
        })
        await this.rebuildPrintPages()
      } finally {
        this.$store.commit('onLoading', false)
      }
    },
    rebuildItemRows () {
      this.pdfCategoryGroups = this.pdfCategoryGroups.map(category => ({
        ...category,
        itemRows: this.chunkItems(category.items, this.pdfLayout.columnCount)
          .map((row, index) => ({
            showCategoryLabel: index === 0,
            items: row
          }))
      }))
    },
    async rebuildPrintPages () {
      const visibleCategoryGroups = this.pdfCategoryGroups.filter(category => {
        return this.selectedCategoryIds.includes(category.categoryId)
      })
      const rowGroups = this.buildRowGroups(visibleCategoryGroups)

      this.isPaginating = true
      this.printPages = rowGroups.length
        ? [{ groups: [] }]
        : []
      await this.$nextTick()
      await this.buildPrintPagesByOverflow(rowGroups)
      this.isPaginating = false
    },
    async buildPrintPagesByOverflow (rowGroups) {
      const pages = []
      let currentGroups = []

      for (const group of rowGroups) {
        const candidateGroups = [
          ...currentGroups,
          group
        ]

        this.printPages = [
          ...pages,
          {groups: candidateGroups}
        ]

        await this.$nextTick()

        const currentPageEl = this.$el.querySelector('.layoutPreview .pdfPage:last-child')

        let isOverflow = false

        if (currentPageEl) {
          const pageRect = currentPageEl.getBoundingClientRect()
          const pageSafeSpace = 6

          const groupEls = Array.from(
            currentPageEl.querySelectorAll('.pdfItemRowBlock')
          )

          const contentBottom = groupEls.reduce((max, el) => {
            const rect = el.getBoundingClientRect()
            return Math.max(max, rect.bottom - pageRect.top)
          }, 0)

          const limit = currentPageEl.clientHeight - pageSafeSpace

          isOverflow = contentBottom > limit
        }

        if (isOverflow && currentGroups.length > 0) {
          pages.push({
            groups: [...currentGroups]
          })

          currentGroups = [group]

          this.printPages = [
            ...pages,
            {
              groups: currentGroups
            }
          ]

          await this.$nextTick()
        } else {
          currentGroups = candidateGroups
        }
      }

      if (currentGroups.length) {
        pages.push({
          groups: [...currentGroups]
        })
      }

      this.printPages = pages
    },
    async getShopPage () {
      try {
        const shopPage = await this.$store.dispatch('shop/getShopPage', this.shopPageId)
        const shopPageInfo = shopPage?.message
        if (shopPageInfo) {
          this.shopPageInfo = shopPageInfo
        }
      } finally {

      }
    },
    async printPdf () {
      await this.rebuildPrintPages()
      await this.$nextTick()
      const coverUrl = this.coverUrl
      const coverHtml = coverUrl
        ? `
          <section class="printCoverPage">
            <iframe
              id="coverFrame"
              src="${coverUrl}"
              style="position:absolute;top:0;left:0;width:100%;height:100%;margin:0;border:0;"
            ></iframe>
          </section>
        `
        : ''

      const printContents = this.$refs.printTarget.innerHTML

      const watermarkPositionCss = this.styleObjectToCss(
        this.getWatermarkStyle('mm')
      )

      const watermarkWidthMm = this.pdfLayout.watermarkSize
        ? Number(this.pdfLayout.watermarkSize) * 210 / 800
        : 30

      const printStyles = `
        @media print {
          * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          @page {
            size: A4;
            margin: 0;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            background: #fff;
          }

          .printCoverPage {
            position: relative;
            width: 210mm;
            height: 297mm;
            overflow: hidden;
            box-sizing: border-box;
            break-after: page;
            page-break-after: always;
          }

          .pdfPage {
            position: relative;
            display: block;
            width: 210mm;
            height: 297mm;
            margin: 0;
            padding: 14mm;
            box-sizing: border-box;
            overflow: hidden;
            break-after: page;
            page-break-after: always;
          }

          .pdfPage:last-child {
            break-after: auto;
            page-break-after: auto;
          }

          .pdfItemRowBlock {
            position: relative;
            z-index: 1;
          }

          .watermark {
            ${watermarkPositionCss}
            pointer-events: none;
          }

          .watermark img {
            display: block;
            max-width: ${watermarkWidthMm}mm;
            height: auto;
          }
        }
      `

      let styles = ''

      for (const sheet of document.styleSheets) {
        try {
          styles += Array.from(sheet.cssRules)
            .map(rule => rule.cssText)
            .join('\n')
        } catch (err) {
          console.warn('Cannot access stylesheet:', sheet.href)
        }
      }

      const waitPrintScript = coverUrl
        ? `
          const frame = document.getElementById('coverFrame');

          frame.onload = function () {
            setTimeout(function () {
              window.print();
              setTimeout(() => window.close(), 500);
            }, 1000);
          };
        `
        : `
          window.onload = function () {
            setTimeout(function () {
              window.print();
              setTimeout(() => window.close(), 500);
            }, 300);
          };
        `

      const printWindow = window.open('', '_blank', 'width=800,height=800')
      const windowTitle = this.getExportFileName()
      printWindow.document.open()
      printWindow.document.write(`
  <html>
    <head>
      <title>${windowTitle}</title>
      <style>
        ${styles}
        ${printStyles}
      </style>
    </head>
    <body>
      ${coverHtml}
      <div>
        ${printContents}
      </div>

      <script>
        ${waitPrintScript}
      <\/script>
    </body>
  </html>
      `)
      printWindow.document.close()
    },
    getExportJsonData () {
      const list = this.pdfCategoryGroups
        .filter(category => this.selectedCategoryIds.includes(category.categoryId))
        .map(category => ({
          categoryId: category.categoryId,
          categoryName: category.categoryName,
          items: category.items
        }))
      return {
        shopName: this.shopPageInfo.pageName,
        shopItems: list
      }
    },
    downloadJson () {
      const data = this.getExportJsonData()
      const fileName = `${this.getExportFileName()}.json`

      const blob = new Blob(
        [JSON.stringify(data, null, 2)],
        { type: 'application/json;charset=utf-8' }
      )

      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')

      a.href = url
      a.download = fileName
      a.click()

      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style lang="scss" scoped>
.layoutContainer {
  flex: 1;
  display: flex;
  gap: 8px;
  align-items: stretch;
  overflow: hidden;
}
.layoutSetting {
  flex: 1;
  overflow: auto;
  position: relative;
  padding: 30px;
  background-color: #fff;
  .btnExport {
    position: absolute;
    top: 30px;
    right: 30px;
    display: flex;
    gap: 4px;
    button {
      width: 42px;
      height: 42px;
      background-position: 50% 50%;
      background-size: contain;
      background-repeat: no-repeat;
    }
    .exportJson {
      background-image: url('~@/assets/img/ico_json.png');
    }
    .exportPdf {
      background-image: url('~@/assets/img/ico_pdf.png');
    }
  }
  .pdfCategoryList {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 4px 8px;
    .checkbox {
      margin: 0;
      line-height: 1.2;
    }
  }
  .setTemplate {
    display: flex;
    gap: 24px;
  }
  .templateItem {
    flex: 1;
    .btns {
      display: flex;
      gap: 2px;
    }
    .btnAlign {
      position: relative;
      width: 38px;
      height: 38px;
      padding: 6px;
      border: 1px solid $color-lgrey2;
      border-radius: 4px;
      &:before {
        content: "";
        display: block;
        width: 24px;
        height: 24px;
        background: url('~@/assets/img/align-center.png') 50% 50% / contain no-repeat;
        opacity: 0.4;
      }
    }
    .active {
      border-color: $color-grey;
      &:before {
        opacity: 0.8;
      }
    }
  }
  .coverPage {
    width: 320px;
    padding: 8px;
    border: 1px solid $color-lgrey2;
    border-radius: 8px;
  }
  .setWatermark {
    display: flex;
    align-items: flex-start;
    gap: 24px;
  }
  .watermarkImage {
    position: relative;
    max-width: 200px;
    min-height: 30px;
    text-align: center;
    border: 1px solid $color-lgrey2;
    .btnDel {
      position: absolute;
      top: 0;
      right: 0;
      width: 30px;
      height: 30px;
      padding: 8px;
      font-size: 0;
      background: rgba(0,0,0,0.2);
    }
  }
  .watermarkPositionPicker {
    position: relative;
    width: 74px;
    height: 82px;
    border: 1px solid $color-lgrey2;
    border-radius: 4px;
    button {
      position: absolute;
      width: 20px;
      height: 20px;
      border: 1px solid $color-lgrey2;
      border-radius: 4px;
    }
    .tl {
      top: 2px;
      left: 2px;
    }
    .tc {
      top: 2px;
      left: 50%;
      margin-left: -10px;
    }
    .tr {
      top: 2px;
      right: 2px;
    }
    .bl {
      bottom: 2px;
      left: 2px;
    }
    .bc {
      bottom: 2px;
      left: 50%;
      margin-left: -10px;
    }
    .br {
      bottom: 2px;
      right: 2px;
    }
    .active {
      background-color: $color-dark;
    }
  }
}
.layoutPreview {
  position: relative;
  width: 800px; // radio: 800*1131
  padding: 40px;
  font-size: 16px;
  overflow: auto;
  background-color: #fff;
  &.isPaginating {
    padding: 0;
    .pdfPage {
      width: 694px;
      height: 1025px;
      margin-bottom: 0;
      padding: 0;
    }
  }
}
.pdfItemRowBlock:first-of-type {
  .pdfCategoryLabel {
    margin-top: 0;
  }
}
.watermark {
  position: absolute;
}
.pdfCategoryLabel {
  margin-top: 32px;
  margin-bottom: 0.4em;
  font-size: 1em;
  font-weight: 700;
}
.pdfItemRowBlock {
  margin-top: 18px;
  break-inside: avoid;
  page-break-inside: avoid;
}
.pdfItemRow {
  display: grid;
  gap: 1em;
}
.pdfItem {
  line-height: 1.2;
  font-size: 1em;
  .thumb {
    height: 0;
    margin-bottom: 0.4em;
    padding-bottom: 74%;
    border: 1px solid rgba(140, 140, 140, 0.2);
    border-radius: 1em;
    background-color: #fff;
    background-size: cover;
    background-position: 50% 50%;
    background-repeat: no-repeat;
  }
  .align_left {
    text-align: left;
  }
  .align_center {
    text-align: center;
  }
  .align_right {
    text-align: right;
  }
}
</style>